import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PageRenderer } from 'magical-trails-react';
import { createDefaultPage, type Page } from 'magical-trails';

// TrailSLAM test harness (route: /ar-test). Renders one world-AR page
// through the real PageRenderer with NO MapLibre map, NO trail modal and NO
// trail state mounted. Everything else (service worker, styles,
// React/three versions) is identical to the player. The AR debug overlay is
// always on here, so its REC/MARK/SAVE strip can record a session for the
// offline replay (docs/AR-TRAILSLAM-TEST-PROTOCOL.md).
//
//   /ar-test                          Creature Photo (default)
//   /ar-test?page=snapChaseAR         Snap Chase AR
//   /ar-test?page=chestHunt           Treasure Chest hunt
//   /ar-test?page=arShooterGame       the AR Shooter this page used to be,
//                                     kept for the WebXR black-screen bisect
//   add &arBackend=trailslam|webxr|fallback to force a backend,
//   &arRecord=1 to record hands-free.
//
// Deliberately not linked from the player; /diagnostics links to it.

const HARNESS_PAGES = {
  creaturePhoto: 'creaturePhoto',
  snapChaseAR: 'snapChaseAR',
  chestHunt: 'treasureChest',
  arShooterGame: 'arShooterGame',
} as const;

type HarnessPage = keyof typeof HARNESS_PAGES;

const isHarnessPage = (value: string | null): value is HarnessPage =>
  value !== null && Object.prototype.hasOwnProperty.call(HARNESS_PAGES, value);

/** On top of the editor defaults: only what the harness needs to differ. */
const HARNESS_OVERRIDES: Partial<Record<HarnessPage, Record<string, unknown>>> =
  {
    arShooterGame: {
      variant: 'pumpkins',
      targetScore: 16,
      title: 'AR isolation test',
      instructions:
        'Diagnostic harness: same AR Shooter, no map or trail underneath.',
    },
  };

const harnessPage = (name: HarnessPage): Page =>
  ({
    ...createDefaultPage(HARNESS_PAGES[name]),
    ...HARNESS_OVERRIDES[name],
  }) as Page;

/**
 * The overlay reads this key when the AR canvas mounts; setting it before
 * the page renders turns it on without the tester typing ?arDebug=1.
 */
const forceArDebug = (): void => {
  try {
    window.sessionStorage.setItem('mt-ar-debug', '1');
  } catch {
    // Storage blocked: ?arDebug=1 in the URL still works.
  }
};

const noop = () => {};

export default function ARTest() {
  const { search } = useLocation();
  const requested = new URLSearchParams(search).get('page');
  const name: HarnessPage = isHarnessPage(requested)
    ? requested
    : 'creaturePhoto';
  const pages = useMemo(() => {
    forceArDebug();
    return [harnessPage(name)];
  }, [name]);

  return (
    <div className="fixed inset-0 bg-black">
      <PageRenderer
        // A new page type is a new session, not an update of the old one.
        key={name}
        pages={pages}
        code="ARTEST"
        currentPage={0}
        handlenextPage={noop}
        handleprevPage={noop}
        handleSetPage={noop}
        answerQuestion={noop}
        trailEvent={noop}
        compareImages={() => Promise.resolve({ match: false, score: 0 })}
      />
    </div>
  );
}
